import { oc } from "@orpc/contract";
import { openapi } from "@orpc/openapi";
import { z } from "zod";
import { visitResultSchema, visitTranscriptCreateSchema } from "./visit";

// A visit's status while it is a pre-chart draft: prepared before the
// patient is seen, with a note and no recording yet. "Start visit" records
// into it; the note is then written from the recording with the pre-chart as
// context, and the status moves on like any recorded visit.
export const PRE_CHART_STATUS = "Pre-chart";

const fields = visitTranscriptCreateSchema.shape;
// Same template fields as a new visit (no recording, duration or language).
export const preChartCreateSchema = z
  .strictObject({
    title: fields.title,
    template: fields.template,
    style: fields.style,
    format: fields.format,
    exam: fields.exam,
    customTemplatePublicId: fields.customTemplatePublicId,
  })
  .refine(
    (value) =>
      value.template === "Custom"
        ? value.customTemplatePublicId !== undefined
        : value.customTemplatePublicId === undefined,
    "Custom template selection is inconsistent",
  );
export type PreChartCreateInput = z.input<typeof preChartCreateSchema>;

// Changing a pre-chart draft's template (clinician feedback 03/11): the same
// template fields, without the title.
export const preChartTemplateSchema = z
  .strictObject({
    template: fields.template,
    style: fields.style,
    format: fields.format,
    exam: fields.exam,
    customTemplatePublicId: fields.customTemplatePublicId,
  })
  .refine(
    (value) =>
      value.template === "Custom"
        ? value.customTemplatePublicId !== undefined
        : value.customTemplatePublicId === undefined,
    "Custom template selection is inconsistent",
  );
export type PreChartTemplateInput = z.input<typeof preChartTemplateSchema>;
export type PreChartTemplateBody = z.output<typeof preChartTemplateSchema>;
// The draft and the new template's headings, in order: the client asks the
// assistant to rewrite the draft under them (reviewed before it is saved).
export const preChartTemplateResultSchema = z.object({
  public_id: visitResultSchema.shape.public_id,
  headings: z.array(z.string().min(1).max(200)).min(1).max(40),
});
export type PreChartCreateBody = z.output<typeof preChartCreateSchema>;

// Which lab values a pre-chart takes from documents (clinician feedback
// 03/11): all of them (the default), abnormal ones only, or only the listed
// tests. The clinician's setting, overridable per pre-chart; sent with each
// assistant edit and used only on a pre-chart draft.
export const PRE_CHART_LAB_MODES = ["all", "abnormal", "tests"] as const;
export const PRE_CHART_LAB_TESTS_MAX = 40;
export const preChartLabsSchema = z
  .strictObject({
    mode: z.enum(PRE_CHART_LAB_MODES),
    tests: z
      .array(z.string().trim().min(1).max(60))
      .max(PRE_CHART_LAB_TESTS_MAX)
      .optional(),
  })
  .refine(
    (value) => value.mode !== "tests" || (value.tests?.length ?? 0) > 0,
    "Name at least one test",
  );
export type PreChartLabs = z.infer<typeof preChartLabsSchema>;
export type PreChartLabMode = (typeof PRE_CHART_LAB_MODES)[number];

export const preChartContract = {
  createPreChart: oc
    .errors({
      BAD_REQUEST: { message: "Invalid request" },
      UNAUTHORIZED: { message: "Authentication required" },
      FORBIDDEN: { message: "Account required" },
      NOT_FOUND: { message: "Template not found" },
      METHOD_NOT_ALLOWED: { message: "Method Not Allowed" },
      CONFLICT: { message: "Submission key was used with different input" },
      INTERNAL_SERVER_ERROR: { message: "Internal Server Error" },
      SERVICE_UNAVAILABLE: { message: "Pre-chart unavailable" },
    })
    .input(
      z.object({
        params: z.strictObject({}).default({}),
        query: z.strictObject({}).default({}),
        headers: z.object({ "idempotency-key": z.uuid().toLowerCase() }),
        body: preChartCreateSchema,
      }),
    )
    .meta(
      openapi({
        method: "POST",
        path: "/api/v2/pre-charts",
        inputStructure: "detailed",
        successStatus: 201,
        spec: (spec) => ({ ...spec, security: [{ bearerAuth: [] }] }),
      }),
    )
    .output(visitResultSchema),
  changePreChartTemplate: oc
    .errors({
      BAD_REQUEST: { message: "Invalid request" },
      UNAUTHORIZED: { message: "Authentication required" },
      FORBIDDEN: { message: "Account required" },
      NOT_FOUND: { message: "Visit or template not found" },
      METHOD_NOT_ALLOWED: { message: "Method Not Allowed" },
      CONFLICT: { message: "The visit is no longer a pre-chart draft" },
      INTERNAL_SERVER_ERROR: { message: "Internal Server Error" },
    })
    .input(
      z.object({
        params: z.strictObject({
          public_id: visitResultSchema.shape.public_id,
        }),
        query: z.strictObject({}).default({}),
        body: preChartTemplateSchema,
      }),
    )
    .meta(
      openapi({
        method: "PUT",
        path: "/api/v2/pre-charts/{public_id}/template",
        inputStructure: "detailed",
        spec: (spec) => ({ ...spec, security: [{ bearerAuth: [] }] }),
      }),
    )
    .output(preChartTemplateResultSchema),
};

// The visit is a pre-chart draft (not recorded yet).
export const isPreChartDraft = (status: string | null | undefined): boolean =>
  status === PRE_CHART_STATUS;
